import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ApiError,
  deleteAccount,
  disconnectMyChat,
  getAccount,
  listMembers,
  listMyConnectedChats,
  setMemberRole,
  signOutEverywhere,
  type AccountBlocker,
  type AccountOrganization,
  type AccountPreview,
  type AuthMethod,
  type AuthenticatedSession,
  type MyConnectedChat,
  type OrgMember,
} from '../api'
import { ThemeToggle } from '../components/ThemeToggle'
import { Wordmark } from '../components/Wordmark'
import { disputeLockSwitchAtBilling } from '../disputeLock'
import { connectedLine, stillActs } from '../connectedChats'

/*
 * A person's own account: who they are signed in as, signing out everywhere,
 * and deleting the account.
 *
 * The app has both, because a phone is lost more easily than a laptop and
 * both app stores require that an account made in an app can be deleted
 * there. The web had neither, so the same person could delete their account
 * from a phone and not from the computer they send from. These are the app's
 * two screens, in the app's words (the plan's section 3.5), on the web.
 *
 * What deleting removes and keeps is said in full before the button, because
 * it is the one thing about this product a person must not be surprised by:
 * docsdone forgets who they are, and never what they did. What they signed and
 * sent is evidence other people rely on, and the account going does not take
 * it with it.
 */

// The words both clients show, word for word (final plan, section 3.5). They
// are the server's account of what store.DeleteAccount does, and are checked
// against it in build-notes/WEB-3.md: identities, identifiers, sessions,
// memberships and participation links go; audit events, participations,
// capabilities and email deliveries are never named by any statement.
const REMOVES =
  'Deleting your account removes how you sign in to docsdone, your membership of every organization, and the list of documents you can open because you signed or received them. You are signed out everywhere. This cannot be undone.'
const KEEPS =
  'Kept, because they are the record of what happened and other people rely on them: every document you sent or signed, and the record of each thing you did to it (your name and address as given, when, the network address, and the app or browser you used). Each organization keeps these for as long as it keeps the document. Each organization you belonged to also keeps its own contact details for you; its administrator can erase them. Records that sign-in codes were sent to your address are kept for security.'
// Shown for any federated sign-in. The session cannot say whether one was a
// company's, and the sentence is conditional, so it stays true for all of them.
const COMPANY_SIGN_IN = 'If your company signs you in to docsdone, signing in with it again creates a new account.'
const FRESH_SIGN_IN = 'For your safety, sign in again, then delete your account within ten minutes.'

const runsAlone = (name: string) =>
  `You are the only member of ${name}, so it is erased with your account: its documents and their signed copies, its templates, its contacts and its open invitations. People you sent documents to can no longer open them through their links. The record of each thing anyone did to those documents is kept.`
// "Turn it off under Billing" only where Billing has the switch: an
// enterprise organization's billing page has none.
const held = (name: string, disputeLock?: boolean, atBilling = false) =>
  disputeLock
    ? `Dispute lock is on for ${name}, so it cannot be erased, and your account cannot be deleted while you are its only member.${atBilling ? ' Turn it off under Billing first.' : ''}`
    : `${name} has a document under legal hold, so it cannot be erased, and your account cannot be deleted while you are its only member.`
const writeTo = (address: string) => `Write to ${address} and docsdone will help.`
const lastAdmin = (name: string) => `Make someone else an administrator of ${name} first.`

/** What a refused deletion says about one organization. */
function blockerSentence(b: AccountBlocker, lockAtBilling: string[]): string {
  switch (b.reason) {
    case 'last_admin':
      return lastAdmin(b.organization_name)
    case 'legal_hold':
      return held(b.organization_name, b.dispute_lock, lockAtBilling.includes(b.organization_id))
    default:
      return `Type ${b.organization_name} exactly as shown to erase it with your account.`
  }
}

/**
 * The address as the server compares it: case and the spaces around it do not
 * count. The button waits for it so a slip of the finger is caught here rather
 * than as a refusal.
 */
const sameAddress = (typed: string, address: string) =>
  typed.trim().toLowerCase() === address.trim().toLowerCase() && address.trim() !== ''

/** An organization's name as the server compares it: exact, apart from the spaces around it. */
const sameName = (typed: string | undefined, name: string) => (typed ?? '').trim() === name.trim()

/** The organizations a person runs alone that can go with the account. */
const erasable = (orgs: AccountOrganization[]) =>
  orgs.filter((o) => o.blocks_deletion === 'sole_member' && !o.legal_hold)

interface Refusal {
  message: string
  blockers?: AccountBlocker[]
  supportEmail?: string
}

// ---------------------------------------------------------------- account

export function Account({
  session,
  onSignedOutEverywhere,
}: {
  session: AuthenticatedSession
  onSignedOutEverywhere: () => void
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const service = session.identity.kind === 'service'

  async function everywhere() {
    setBusy(true)
    setError('')
    try {
      await signOutEverywhere()
      onSignedOutEverywhere()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not sign you out everywhere.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="page page-narrow account-page">
      <div className="page-head">
        <h1>Account</h1>
      </div>

      {/* Each label above its value, as the join page's accounts are. A
          table put them side by side, which at 390px left an address a
          third of the card, and its cell padding set the text 11px in from
          every other card's at 1440px. */}
      <div className="card">
        <dl className="account-pair">
          <dt>Name</dt>
          <dd className="account-name"><strong>{session.identity.display_name}</strong></dd>
          <dt>Sign-in address</dt>
          <dd className="account-address" id="account-address">
            {service ? 'The service credential' : session.identity.email ?? '—'}
          </dd>
        </dl>
      </div>

      <div className="card" id="account-organizations">
        <h2>Organizations</h2>
        {session.memberships.length === 0 ? (
          <p className="muted">You do not belong to an organization.</p>
        ) : (
          <ul className="account-orgs">
            {session.memberships.map((m) => (
              <li key={m.organization_id} className="row-between">
                <span className="account-name">{m.organization_name}</span>
                <span className="tag">{m.role === 'admin' ? 'Administrator' : 'Member'}</span>
              </li>
            ))}
          </ul>
        )}
        {/* True because an invitation becomes a membership only when its
            address signs in (signin.go and federated.go are the only callers
            of acceptInviteTx), so a session opened before it never sees it.
            The service credential has no address to invite. */}
        {!service && (
          <p className="small muted account-last">Invited to another organization? Sign out and sign in again to join it.</p>
        )}
      </div>

      {!service && <YourChats session={session} />}

      <div className="card" id="sign-out-everywhere">
        <h2>Sign out everywhere</h2>
        <p>
          Signs you out in every browser and in the app, this one included, and disconnects every
          chat you connected. Use it if a computer or a phone you signed in on is lost, or is not yours.
        </p>
        {/* revoke-all ends every session of the person, whichever way each
            was signed in (B13: RevokeSessionsForParty), so "everywhere" is
            true without a caveat. It said once that it reached only the way
            this browser signed in, which was true before B13. It also ends
            every chat the person connected (endSignInsTx revokes their
            mcp_grants, 81cff5b); before that a chat on a lost laptop went on
            acting as them. */}
        {error && <div className="notice notice-bad account-gap">{error}</div>}
        <button id="sign-out-everywhere-button" className="btn btn-secondary" disabled={busy} onClick={() => void everywhere()}>
          {busy ? 'Signing out…' : 'Sign out everywhere'}
        </button>
      </div>

      {service ? (
        <div className="card" id="delete-my-account">
          <h2>Delete my account</h2>
          <p className="muted account-last">The service credential cannot be deleted here.</p>
        </div>
      ) : (
        <div className="card danger-zone" id="delete-my-account">
          <h2>Delete my account</h2>
          <p className="small muted">The next page says what is removed and what is kept, before anything happens.</p>
          <Link className="btn btn-danger" to="/account/delete">Delete my account</Link>
        </div>
      )}
    </main>
  )
}

// ---------------------------------------------------------------- chats

/**
 * The chats this person connected, which they can end themselves. Only an
 * administrator could once, on the organization's settings, so a member whose
 * laptop was lost had no way to stop a chat acting as them.
 */
function YourChats({ session }: { session: AuthenticatedSession }) {
  const [grants, setGrants] = useState<MyConnectedChat[] | null>(null)
  const [busy, setBusy] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    let live = true
    listMyConnectedChats()
      .then((r) => { if (live) setGrants(r.grants ?? []) })
      .catch((e: unknown) => { if (live) setError(e instanceof Error ? e.message : 'Could not read your connected chats.') })
    return () => { live = false }
  }, [])

  async function disconnect(id: string) {
    setBusy(id)
    setError('')
    try {
      await disconnectMyChat(id)
      setGrants((rows) => (rows ?? []).filter((g) => g.id !== id))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not disconnect that chat.')
    } finally {
      setBusy('')
    }
  }

  const orgName = (id?: string) => session.memberships.find((m) => m.organization_id === id)?.organization_name
  return (
    <YourChatsView grants={grants} orgName={orgName} busy={busy} error={error} onDisconnect={(id) => void disconnect(id)} />
  )
}

/**
 * A connection ends when it is disconnected (Sign out everywhere disconnects
 * every one) or when its 90 days run out, whichever is first; using the chat
 * does not extend it. It said "until you disconnect it", which an ended chat
 * still listed under it contradicted. Chats whose 90 days have run out are
 * left out: they no longer act as anybody.
 */
export function YourChatsView({ grants, orgName, busy, error, onDisconnect, now = Date.now() }: {
  /** null while asking. */
  grants: MyConnectedChat[] | null
  orgName: (organizationId?: string) => string | undefined
  busy: string
  error: string
  onDisconnect: (id: string) => void
  now?: number
}) {
  const live = grants && grants.filter((g) => stillActs(g, now))
  return (
    <div className="card" id="your-chats">
      <h2>Connected chats</h2>
      <p className="small muted">
        A chat you connected acts as you at one organization until it is disconnected or its 90 days run out.
      </p>
      {error && <div className="notice notice-bad account-gap">{error}</div>}
      {live === null ? (
        !error && <p className="muted account-last">Loading…</p>
      ) : live.length === 0 ? (
        <p className="muted account-last">You have not connected a chat.</p>
      ) : (
        <ul className="account-orgs">
          {live.map((g) => {
            const at = orgName(g.organization_id)
            return (
              <li key={g.id} className="row-between">
                <span className="account-name">
                  <strong>{g.client_name}</strong>
                  <span className="small muted account-chat-when">
                    {connectedLine(g, at)}
                  </span>
                </span>
                <button
                  className="btn btn-secondary btn-small"
                  type="button"
                  aria-label={`Disconnect ${g.client_name}${at ? ` at ${at}` : ''}`}
                  disabled={busy !== ''}
                  onClick={() => onDisconnect(g.id)}
                >
                  {busy === g.id ? 'Disconnecting…' : 'Disconnect'}
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

// ---------------------------------------------------------------- delete

/**
 * The deletion page: it asks the server what deleting would do first, and
 * the server, not this page, decides whether it may happen. The page only
 * keeps the button off until what the server would refuse has been dealt with.
 */
export function DeleteAccount({
  session,
  onSignInAgain,
  onDeleted,
}: {
  session: AuthenticatedSession
  onSignInAgain: (address: string) => void
  onDeleted: () => void
}) {
  const service = session.identity.kind === 'service'
  const [preview, setPreview] = useState<AccountPreview | null>(null)
  const [loadError, setLoadError] = useState('')
  const [members, setMembers] = useState<Record<string, OrgMember[] | 'failed'>>({})
  const [typedAddress, setTypedAddress] = useState('')
  const [typedNames, setTypedNames] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)
  const [promoting, setPromoting] = useState('')
  const [refusal, setRefusal] = useState<Refusal | null>(null)
  const [notice, setNotice] = useState('')

  const load = useCallback(() => {
    getAccount()
      .then((p) => {
        setPreview(p)
        setLoadError('')
      })
      .catch((e: unknown) => setLoadError(e instanceof Error ? e.message : 'Could not read your account.'))
  }, [])

  // Not asked for the service credential: the server refuses it as
  // not_a_person, and the page already knows that from the session.
  useEffect(() => {
    if (!service) load()
  }, [service, load])

  // Who else belongs to each organization this person is the last
  // administrator of, so one of them can be made an administrator here.
  const lastAdminOf = (preview?.organizations ?? []).filter((o) => o.blocks_deletion === 'last_admin')
  const unread = lastAdminOf.map((o) => o.organization_id).filter((id) => !(id in members)).join(' ')
  useEffect(() => {
    if (!unread) return
    for (const id of unread.split(' ')) {
      listMembers(id)
        .then((r) => setMembers((m) => ({ ...m, [id]: r.members })))
        .catch(() => setMembers((m) => ({ ...m, [id]: 'failed' })))
    }
  }, [unread])

  async function makeAdmin(org: AccountOrganization, member: OrgMember) {
    setPromoting(member.party_id)
    setNotice('')
    setRefusal(null)
    try {
      await setMemberRole(org.organization_id, member.party_id, 'admin')
      setNotice(`${member.display_name} is now an administrator of ${org.organization_name}.`)
      load()
    } catch (e) {
      setRefusal({ message: e instanceof Error ? e.message : 'Could not change their role.' })
    } finally {
      setPromoting('')
    }
  }

  async function remove() {
    if (!preview) return
    setBusy(true)
    setRefusal(null)
    setNotice('')
    try {
      await deleteAccount(
        typedAddress,
        erasable(preview.organizations).map((o) => ({
          organization_id: o.organization_id,
          name: typedNames[o.organization_id] ?? '',
        })),
      )
      onDeleted()
    } catch (e) {
      if (e instanceof ApiError && e.code === 'fresh_sign_in_required') {
        // The ten minutes ran out while the page was open. The page turns
        // into the one that says so, rather than showing a refusal beside a
        // button that can only be refused again.
        setPreview({ ...preview, fresh_sign_in_required: true })
      } else if (e instanceof ApiError) {
        setRefusal({ message: e.message, blockers: e.blockers, supportEmail: e.supportEmail })
        // Whatever stopped it may have changed since the page was read.
        if (e.code === 'account_deletion_blocked' || e.code === 'not_sole_member' || e.code === 'erasure_raced') load()
      } else {
        setRefusal({ message: 'docsdone did not answer. Reload this page to see whether your account was deleted.' })
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="page page-narrow account-page">
      <div className="page-head">
        <h1>Delete my account</h1>
      </div>
      {service ? (
        <div className="notice notice-info">The service credential cannot be deleted here.</div>
      ) : loadError ? (
        <div className="notice notice-bad">{loadError}</div>
      ) : preview === null ? (
        <p className="muted">Loading…</p>
      ) : (
        <DeleteAccountView
          preview={preview}
          authMethod={session.auth_method}
          supportEmail={session.support_email}
          lockAtBilling={session.memberships
            .map((m) => m.organization_id)
            .filter((id) => disputeLockSwitchAtBilling(session, id))}
          you={session.identity.party_id}
          members={members}
          typedAddress={typedAddress}
          typedNames={typedNames}
          busy={busy}
          promoting={promoting}
          refusal={refusal}
          notice={notice}
          onTypeAddress={setTypedAddress}
          onTypeName={(id, name) => setTypedNames((t) => ({ ...t, [id]: name }))}
          onMakeAdmin={(org, member) => void makeAdmin(org, member)}
          onSignInAgain={onSignInAgain}
          onDelete={() => void remove()}
        />
      )}
    </main>
  )
}

interface DeleteAccountViewProps {
  preview: AccountPreview
  authMethod: AuthMethod
  supportEmail?: string
  /** The organizations whose Billing page has a dispute lock switch (disputeLockSwitchAtBilling). */
  lockAtBilling: string[]
  /** The person's own party, so the list of who can be made an administrator leaves them out. */
  you: string
  /** Members of each organization the person is the last administrator of, once read. */
  members: Record<string, OrgMember[] | 'failed'>
  typedAddress: string
  typedNames: Record<string, string>
  busy: boolean
  /** The party being made an administrator right now, or ''. */
  promoting: string
  refusal: Refusal | null
  notice: string
  onTypeAddress: (typed: string) => void
  onTypeName: (organizationId: string, typed: string) => void
  onMakeAdmin: (org: AccountOrganization, member: OrgMember) => void
  onSignInAgain: (address: string) => void
  onDelete: () => void
}

/**
 * Everything the deletion page shows once the server has answered, in the
 * order the app shows it: what has to be dealt with first, the reason to sign
 * in again, what is removed and kept, each organization that goes with the
 * account, and the address typed out.
 */
export function DeleteAccountView({
  preview,
  authMethod,
  supportEmail,
  lockAtBilling,
  you,
  members,
  typedAddress,
  typedNames,
  busy,
  promoting,
  refusal,
  notice,
  onTypeAddress,
  onTypeName,
  onMakeAdmin,
  onSignInAgain,
  onDelete,
}: DeleteAccountViewProps) {
  const lastAdminOf = preview.organizations.filter((o) => o.blocks_deletion === 'last_admin')
  const heldBy = preview.organizations.filter((o) => o.blocks_deletion === 'sole_member' && o.legal_hold)
  const goesWith = erasable(preview.organizations)
  const recent = !preview.fresh_sign_in_required
  const ready =
    recent &&
    !busy &&
    lastAdminOf.length === 0 &&
    heldBy.length === 0 &&
    goesWith.every((o) => sameName(typedNames[o.organization_id], o.organization_name)) &&
    sameAddress(typedAddress, preview.address)

  return (
    <>
      {notice && <div className="notice notice-good account-gap">{notice}</div>}

      {(lastAdminOf.length > 0 || heldBy.length > 0) && (
        <section id="account-blockers" className="account-gap">
          <h2>Before you can delete your account</h2>
          {lastAdminOf.map((org) => {
            const listed = members[org.organization_id]
            const others = Array.isArray(listed)
              ? listed.filter((m) => m.party_id !== you && m.kind !== 'service')
              : []
            return (
              <div key={org.organization_id} className="card account-blocker" data-reason="last_admin">
                <h3 className="account-name">{org.organization_name}</h3>
                <p>{lastAdmin(org.organization_name)}</p>
                {listed === undefined ? (
                  <p className="muted small account-last">Loading its members…</p>
                ) : listed === 'failed' || others.length === 0 ? (
                  <p className="small account-last">
                    {listed === 'failed' ? `Could not read the members of ${org.organization_name}. ` : ''}
                    <Link to={`/orgs/${org.organization_id}/members`}>Open its members</Link>
                  </p>
                ) : (
                  <ul className="account-orgs">
                    {others.map((m) => (
                      <li key={m.party_id} className="row-between">
                        <span className="account-name">{m.display_name}</span>
                        {m.role === 'admin' ? (
                          <span className="tag">Administrator</span>
                        ) : (
                          <button
                            className="btn btn-secondary btn-small"
                            aria-label={`Make ${m.display_name} an administrator of ${org.organization_name}`}
                            disabled={promoting !== ''}
                            onClick={() => onMakeAdmin(org, m)}
                          >
                            {promoting === m.party_id ? 'Working…' : 'Make administrator'}
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )
          })}
          {heldBy.map((org) => (
            <div key={org.organization_id} className="card account-blocker" data-reason="legal_hold">
              <h3 className="account-name">{org.organization_name}</h3>
              <p className={supportEmail ? '' : 'account-last'}>{held(org.organization_name, org.dispute_lock, lockAtBilling.includes(org.organization_id))}</p>
              {supportEmail && <p className="account-last account-address">{writeTo(supportEmail)}</p>}
            </div>
          ))}
        </section>
      )}

      {!recent && (
        <div id="fresh-sign-in" className="notice notice-warn account-gap">
          <p>{FRESH_SIGN_IN}</p>
          <button className="btn" type="button" onClick={() => onSignInAgain(preview.address)}>Sign in again</button>
        </div>
      )}

      <div className="card" id="what-deleting-does">
        <p>{REMOVES}</p>
        <p className={authMethod === 'federated_oidc' ? '' : 'account-last'}>{KEEPS}</p>
        {authMethod === 'federated_oidc' && <p className="account-last">{COMPANY_SIGN_IN}</p>}
      </div>

      {goesWith.map((org) => (
        <div key={org.organization_id} className="card account-erase" data-organization={org.organization_id}>
          <h3 className="account-name">{org.organization_name}</h3>
          <p>{runsAlone(org.organization_name)}</p>
          <label htmlFor={`erase-name-${org.organization_id}`}>
            Type <strong className="account-address">{org.organization_name}</strong> to erase it with your account
          </label>
          <input
            id={`erase-name-${org.organization_id}`}
            type="text"
            autoComplete="off"
            spellCheck={false}
            disabled={!recent}
            value={typedNames[org.organization_id] ?? ''}
            onChange={(e) => onTypeName(org.organization_id, e.target.value)}
          />
        </div>
      ))}

      <div className="card account-confirm">
        <label htmlFor="delete-confirm">
          Type <strong className="account-address">{preview.address}</strong> to confirm
        </label>
        <input
          id="delete-confirm"
          type="text"
          inputMode="email"
          autoComplete="off"
          autoCapitalize="none"
          spellCheck={false}
          disabled={!recent}
          value={typedAddress}
          onChange={(e) => onTypeAddress(e.target.value)}
        />
        {refusal && (
          <div className="notice notice-bad account-refusal" role="alert" id="delete-refusal">
            <p>{refusal.message}</p>
            {refusal.blockers && refusal.blockers.length > 0 && (
              <ul>
                {refusal.blockers.map((b) => (
                  <li key={b.organization_id}>{blockerSentence(b, lockAtBilling)}</li>
                ))}
              </ul>
            )}
            {refusal.supportEmail && <p>{writeTo(refusal.supportEmail)}</p>}
          </div>
        )}
        <div className="row account-actions">
          <Link className="btn btn-secondary" to="/account">Cancel</Link>
          <button id="delete-account" className="btn btn-danger" type="button" disabled={!ready} onClick={onDelete}>
            {busy ? 'Deleting…' : 'Delete my account'}
          </button>
        </div>
      </div>
    </>
  )
}

// ---------------------------------------------------------------- after

export type Farewell = 'deleted' | 'signed_out_everywhere'

/**
 * What the browser shows once its session has gone on purpose. Without it,
 * both acts would end on the ordinary sign-in page, which says nothing about
 * whether what was asked for happened.
 */
export function AccountFarewell({ kind, onContinue }: { kind: Farewell; onContinue: () => void }) {
  const deleted = kind === 'deleted'
  return (
    <div className="signin-wrap">
      <div id="account-farewell" className="card signin-card" data-kind={kind}>
        <div className="signin-logo">
          <Wordmark />
        </div>
        <ThemeToggle />
        <h1>{deleted ? 'Your account has been deleted' : 'You are signed out everywhere'}</h1>
        <p className="small muted">
          {deleted
            ? 'You are signed out on every device.'
            : 'Every browser and app you were signed in on has to sign in again, this one too. Every chat you connected has to be connected again.'}
        </p>
        <button id="farewell-continue" className="btn btn-block signin-submit" type="button" onClick={onContinue}>
          {deleted ? 'Done' : 'Sign in again'}
        </button>
      </div>
    </div>
  )
}
